import { putBlob } from "./engine";
import { PHOTO_MAX_BYTES, PHOTO_SIZE_HINT, compressImageFile } from "../utils/imageCompress";

const KEY = "dh_j_client_docs";
const DOC_MAX_BYTES = 2 * 1024 * 1024;

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function writeAll(rows) {
  localStorage.setItem(KEY, JSON.stringify(rows));
}

export function listClientDocuments(ownerId, role) {
  if (!ownerId) return [];
  return readAll()
    .filter((row) => row.owner_id === ownerId && (!role || row.role === role))
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
}

export function listDocumentsForOwners(ownerIds = [], { kind } = {}) {
  const allowed = new Set(ownerIds.filter(Boolean));
  if (!allowed.size) return [];
  return readAll()
    .filter((row) => allowed.has(row.owner_id) && (!kind || row.kind === kind))
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
}

export function listAllDocuments() {
  return readAll().sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
}

export async function addClientDocument({ ownerId, role, file, deskAccountId, kind }) {
  if (!ownerId || !file) {
    return Promise.reject(new Error("Choose a file to save with this account."));
  }

  let nextFile = file;
  const isPhoto = String(file.type || "").startsWith("image/") || kind === "photo";
  if (isPhoto) {
    try {
      nextFile = await compressImageFile(file);
    } catch (error) {
      return Promise.reject(error);
    }
    if (nextFile.size > PHOTO_MAX_BYTES) {
      return Promise.reject(new Error(PHOTO_SIZE_HINT));
    }
  } else if (file.size > DOC_MAX_BYTES) {
    return Promise.reject(new Error("Please keep documents under 2 MB. Photos may be up to 5 MB."));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("That file could not be read."));
    reader.onload = () => {
      const id = crypto.randomUUID();
      const path = putBlob(`local://docs/${ownerId}/${id}`, reader.result);
      const row = {
        id,
        owner_id: ownerId,
        desk_account_id: deskAccountId || ownerId,
        role: role || "buyer",
        kind: isPhoto ? "photo" : kind || "document",
        file_name: nextFile.name,
        mime_type: nextFile.type || "application/octet-stream",
        size: nextFile.size,
        file_path: path,
        created_at: new Date().toISOString(),
      };
      writeAll([...readAll(), row]);
      resolve(row);
    };
    reader.readAsDataURL(nextFile);
  });
}
